import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { LayoutDashboard, Package, ShoppingCart, Users, ShieldAlert, QrCode, Receipt, Boxes, Tag, LogOut, Menu, X, FileText, Settings, CreditCard, Percent, Landmark, Star, ImageDown } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, perm: "view_dashboard" },
  { to: "/admin/products", label: "Products", icon: Package, perm: "view_products" },
  { to: "/admin/inventory", label: "Inventory", icon: Boxes, perm: "view_inventory" },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart, perm: "view_orders" },
  { to: "/admin/invoices", label: "Invoices", icon: FileText, perm: "view_orders" },
  { to: "/admin/pos", label: "Billing / POS", icon: Receipt, perm: "create_bills" },
  { to: "/admin/customers", label: "Customers", icon: Users, perm: "view_customers" },
  { to: "/admin/staff", label: "Staff & Roles", icon: Users, perm: "manage_staff" },
  { to: "/admin/qr", label: "QR Authenticity", icon: QrCode, perm: "manage_qr" },
  { to: "/admin/coupons", label: "Coupons", icon: Tag, perm: "manage_coupons" },
  { to: "/admin/payments", label: "Payments", icon: CreditCard, perm: "manage_payments" },
  { to: "/admin/sale", label: "Sale", icon: Percent, perm: "manage_sale" },
  { to: "/admin/emi", label: "EMI", icon: Landmark, perm: "view_emi" },
  { to: "/admin/reviews", label: "Reviews", icon: Star, perm: "view_reviews" },
  { to: "/admin/epics", label: "e-Pics", icon: ImageDown, perm: "view_orders" },
  { to: "/admin/audit", label: "Audit Logs", icon: ShieldAlert, perm: "view_audit" },
];

export default function AdminLayout() {
  const { user, loading, hasPerm, logout } = useAuth();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    if (!loading && (!user || user.role === "customer")) nav("/admin/login");
  }, [user, loading]);
  if (!user || user.role === "customer") return null;
  const visible = NAV.filter((n) => hasPerm(n.perm));
  const NavLinks = ({ onNavigate }) => (
    <nav className="flex-1 mt-8 space-y-1">
      {visible.map((n) => (
        <NavLink key={n.to} to={n.to} onClick={onNavigate} data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]/g,'-')}`} className={({ isActive }) => `flex items-center gap-3 px-3 py-3 text-sm transition ${isActive ? "bg-[#C5A059] text-[#121212]" : "text-[#F4F4F5] hover:bg-[#27272A]"}`}>
          <n.icon size={16} /> {n.label}
        </NavLink>
      ))}
    </nav>
  );
  return (
    <div className="admin-shell md:flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 border-r border-[#27272A] p-6 sticky top-0 h-screen flex-col flex-shrink-0">
        <div className="font-serif text-xl tracking-[0.3em] text-[#C5A059]">LEE BAG</div>
        <p className="text-[10px] uppercase tracking-widest text-[#8C867D] mt-1">Admin</p>
        <NavLinks />
        <div className="border-t border-[#27272A] pt-4">
          <LanguageSwitcher dark />
          <p className="text-xs mt-3">{user.name}</p>
          <p className="text-[10px] text-[#8C867D] uppercase tracking-widest">{user.role.replace(/_/g," ")}</p>
          <button onClick={async () => { await logout(); nav("/admin/login"); }} className="mt-3 text-xs flex items-center gap-2 text-[#8C867D] hover:text-white" data-testid="admin-logout"><LogOut size={14} /> Sign out</button>
        </div>
      </aside>
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-[#18181B] border-b border-[#27272A] px-4 h-14 flex items-center justify-between">
        <button onClick={() => setMenuOpen(true)} data-testid="admin-mobile-menu"><Menu size={22} className="text-[#C5A059]" /></button>
        <div className="font-serif text-lg tracking-[0.3em] text-[#C5A059]">LEE BAG</div>
        <div className="flex items-center gap-4">
          <LanguageSwitcher dark />
          <button onClick={async () => { await logout(); nav("/admin/login"); }} data-testid="admin-mobile-logout"><LogOut size={18} /></button>
        </div>
      </div>
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-[#0F0F10] flex flex-col p-6">
          <button onClick={() => setMenuOpen(false)} className="self-end" data-testid="admin-mobile-close"><X size={24} /></button>
          <div className="font-serif text-xl tracking-[0.3em] text-[#C5A059]">LEE BAG</div>
          <p className="text-[10px] uppercase tracking-widest text-[#8C867D] mt-1">{user.name} · {user.role.replace(/_/g," ")}</p>
          <NavLinks onNavigate={() => setMenuOpen(false)} />
        </div>
      )}
      <main className="flex-1 p-4 md:p-8 pt-20 md:pt-8 overflow-x-auto min-w-0"><Outlet /></main>
    </div>
  );
}
